import axios from 'axios';
import { API_BASE_URL, TOKEN_KEYS } from '../config/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

function getAccessToken() {
  return localStorage.getItem(TOKEN_KEYS.access);
}

function getRefreshToken() {
  return localStorage.getItem(TOKEN_KEYS.refresh);
}

export function setTokens(accessToken, refreshToken) {
  if (accessToken) localStorage.setItem(TOKEN_KEYS.access, accessToken);
  else localStorage.removeItem(TOKEN_KEYS.access);
  if (refreshToken) localStorage.setItem(TOKEN_KEYS.refresh, refreshToken);
  else localStorage.removeItem(TOKEN_KEYS.refresh);
}

export function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEYS.access);
  localStorage.removeItem(TOKEN_KEYS.refresh);
  localStorage.removeItem(TOKEN_KEYS.staff);
  localStorage.removeItem(TOKEN_KEYS.permissions);
  localStorage.removeItem(TOKEN_KEYS.isSuperAdmin);
  localStorage.removeItem('utkarsh_admin_token');
}

function isPortalOrPublicUrl(url = '') {
  const u = String(url);
  return u.includes('/public/') || u.includes('/vendor-portal/');
}

api.interceptors.request.use((config) => {
  // Vendor portal / public flows use their own tokens — never require staff JWT.
  if (isPortalOrPublicUrl(config.url)) {
    return config;
  }
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

function isAuthEndpoint(url = '') {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  const { data } = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  const payload = data?.data;
  if (!payload?.accessToken) {
    throw new Error('Refresh failed');
  }
  setTokens(payload.accessToken, payload.refreshToken || refreshToken);
  if (payload.staff) {
    localStorage.setItem(TOKEN_KEYS.staff, JSON.stringify(payload.staff));
  }
  if (payload.permissions) {
    localStorage.setItem(TOKEN_KEYS.permissions, JSON.stringify(payload.permissions));
  }
  if (typeof payload.isSuperAdmin === 'boolean') {
    localStorage.setItem(TOKEN_KEYS.isSuperAdmin, String(payload.isSuperAdmin));
  }
  return payload.accessToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      !original ||
      error.response?.status !== 401 ||
      original._retry ||
      isAuthEndpoint(original.url) ||
      isPortalOrPublicUrl(original.url)
    ) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const accessToken = await refreshPromise;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${accessToken}`;
      return api(original);
    } catch {
      clearAuthStorage();
      return Promise.reject(error);
    }
  },
);

/** Unwrap `{ data, message, error }` → `data`. */
function unwrap(response) {
  return response?.data?.data;
}

export function getApiErrorMessage(error, fallback = 'Request failed') {
  const body = error?.response?.data;
  if (typeof body?.message === 'string' && body.message) return body.message;
  if (typeof body?.error === 'string' && body.error) return body.error;
  if (typeof error?.message === 'string' && error.message) return error.message;
  return fallback;
}

export async function get(url, config) {
  const res = await api.get(url, config);
  return unwrap(res);
}

export async function post(url, body, config) {
  const res = await api.post(url, body, config);
  return unwrap(res);
}

export async function put(url, body, config) {
  const res = await api.put(url, body, config);
  return unwrap(res);
}

export async function patch(url, body, config) {
  const res = await api.patch(url, body, config);
  return unwrap(res);
}

export async function del(url, config) {
  const res = await api.delete(url, config);
  return unwrap(res);
}

/**
 * GET that returns a raw blob (e.g. Excel export).
 * On API JSON errors returned as blobs, parses message and throws.
 */
export async function getBlob(url, config = {}) {
  try {
    const res = await api.get(url, { ...config, responseType: 'blob' });
    const contentType = String(res.headers?.['content-type'] || '');
    if (contentType.includes('application/json')) {
      const text = await res.data.text();
      let message = 'Request failed';
      try {
        const json = JSON.parse(text);
        message = json?.message || json?.error || message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    return res;
  } catch (error) {
    // Already converted to a plain Error (e.g. JSON body on 200 / local throw).
    if (error instanceof Error && !error.response) {
      throw error;
    }
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const json = JSON.parse(text);
        throw new Error(
          json?.message || json?.error || getApiErrorMessage(error),
        );
      } catch (inner) {
        if (inner instanceof Error && !inner.response) throw inner;
      }
    }
    throw new Error(getApiErrorMessage(error));
  }
}
